import React, { useState, useEffect, useRef, useCallback } from "react";
import "./table.scss";
import { FiChevronUp, FiChevronDown, FiSearch } from "react-icons/fi";
import Checkbox from "../Checkbox";
import Input from "../Input";
import Button from "../Button";

/** Flatten Tr children so fragment-wrapped cells (<>...</>) align with column indices. */
function flattenRowCellChildren(row) {
  const cells = [];
  React.Children.forEach(row.props?.children, (child) => {
    if (child == null || child === false) return;
    if (child.type === React.Fragment) {
      React.Children.forEach(child.props?.children, (nested) => {
        if (nested != null && nested !== false) cells.push(nested);
      });
    } else {
      cells.push(child);
    }
  });
  return cells;
}

/** Plain text from cell children so search works with nested elements (e.g. <strong>). */
function textFromReactNode(node) {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number")
    return String(node);
  if (Array.isArray(node)) return node.map(textFromReactNode).join(" ");
  if (React.isValidElement(node))
    return textFromReactNode(node.props?.children);
  return "";
}

// Pagination bileşeni
const Pagination = ({
  total,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const from = currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, total);

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        <span>Sayfa başına kayıt:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span>
          {total} kayıttan {from}-{to} arası
        </span>
      </div>
      <div className="pagination-actions">
        <button
          onClick={() => onPageChange(0)}
          disabled={currentPage === 0}
          title="İlk Sayfa"
        >
          ⟪
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          title="Önceki Sayfa"
        >
          ⟨
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          title="Sonraki Sayfa"
        >
          ⟩
        </button>
        <button
          onClick={() => onPageChange(totalPages - 1)}
          disabled={currentPage >= totalPages - 1}
          title="Son Sayfa"
        >
          ⟫
        </button>
      </div>
    </div>
  );
};

// Tablo başlık hücresi
const Th = ({
  children,
  sortable,
  sortDirection,
  onSort,
  width,
  onResize,
  ...props
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const resizerRef = useRef(null);
  const thRef = useRef(null);
  const isResizingRef = useRef(false);

  const startResizing = useCallback(
    (e) => {
      setIsResizing(true);
      isResizingRef.current = true;
      const startX = e.pageX;
      const startWidth = thRef.current?.offsetWidth || 0;

      const handleMouseMove = (e) => {
        if (isResizingRef.current) {
          const newWidth = startWidth + (e.pageX - startX);
          if (newWidth >= 50) {
            onResize?.(newWidth);
          }
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        isResizingRef.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [onResize]
  );

  return (
    <th
      {...props}
      ref={thRef}
      className={`${sortable ? "sortable" : ""} ${
        isResizing ? "resizing" : ""
      }`}
      style={{ width: width ? `${width}px` : "auto" }}
    >
      <div
        className="th-content"
        onClick={(e) => {
          if (sortable) {
            e.stopPropagation();
            onSort();
          }
        }}
      >
        {children}
        {sortable && (
          <div className="sort-icons">
            <FiChevronUp
              className={`sort-icon ${sortDirection === "asc" ? "active" : ""}`}
            />
            <FiChevronDown
              className={`sort-icon ${
                sortDirection === "desc" ? "active" : ""
              }`}
            />
          </div>
        )}
      </div>
      <div
        ref={resizerRef}
        className={`resizer ${isResizing ? "resizing" : ""}`}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          startResizing(e);
        }}
      />
    </th>
  );
};

// Tablo veri hücresi
const Td = ({ children, ...props }) => <td {...props}>{children}</td>;

// Tablo satırı
const Tr = ({ children, data, ...props }) => <tr {...props}>{children}</tr>;

// Ana tablo bileşeni
const Table = ({
  title,
  columns,
  children,
  footer = null,
  className = "",
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 10,
  hideFooter = false,
  loading = false,
  visibleColumns = null,
  selectAll = false,
  onSelectAll,
  showSelectAll = false,
  actions,
  viewOptions,
  search = true,
  externalSearch = false,
  onSearch,
  searchPlaceholder = "Ara...",
  searchValue = "",
  externalSorting = false,
  onSort,
  ...props
}) => {
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState(searchValue || "");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const [columnWidths, setColumnWidths] = useState({});

  // Sync searchInput with external searchValue prop
  useEffect(() => {
    if (externalSearch && searchValue !== undefined) {
      setSearchInput(searchValue);
    }
  }, [searchValue, externalSearch]);

  // Filter columns based on visibleColumns if provided
  const displayColumns =
    visibleColumns !== null
      ? columns.filter((col) => visibleColumns.includes(col.id))
      : columns;

  useEffect(() => {
    setSortConfig({ key: null, direction: null });
    setCurrentPage(0);
  }, [displayColumns.length]);

  const rows = React.Children.toArray(children);
  const total = rows.length;

  // Sıralama işlevi
  const handleSort = (columnIndex) => {
    const column = displayColumns[columnIndex];
    if (column.sortable === false) return;

    setSortConfig((prevConfig) => {
      const direction =
        prevConfig.key === columnIndex && prevConfig.direction === "asc"
          ? "desc"
          : "asc";
      if (externalSorting && onSort && column.sortKey) {
        onSort({ sortBy: column.sortKey, sortOrder: direction });
      }
      return { key: columnIndex, direction };
    });
  };

  // Sıralama ve filtreleme işlemleri
  const sortedAndFilteredRows = React.useMemo(() => {
    let processedRows = [...rows];

    // Arama filtrelemesi - only if not external search
    if (!externalSearch && searchQuery) {
      const q = searchQuery.toLowerCase();
      processedRows = processedRows.filter((row) => {
        const rowText = flattenRowCellChildren(row)
          .map((cell) => textFromReactNode(cell?.props?.children))
          .join(" ")
          .toLowerCase();
        return rowText.includes(q);
      });
    }

    // Sıralama
    if (!externalSorting && sortConfig.key !== null) {
      processedRows.sort((a, b) => {
        const column = displayColumns[sortConfig.key];
        if (!column || column.sortable === false) return 0;

        let comparison = 0;

        if (column.customSorter) {
          comparison = column.customSorter(
            a.props?.data ?? a.props,
            b.props?.data ?? b.props,
          );
        } else {
          const aChildren = flattenRowCellChildren(a);
          const bChildren = flattenRowCellChildren(b);
          const aCell = aChildren[sortConfig.key];
          const bCell = bChildren[sortConfig.key];
          const aValue = aCell ? textFromReactNode(aCell.props?.children) : "";
          const bValue = bCell ? textFromReactNode(bCell.props?.children) : "";

          switch (column.dataType) {
            case "number":
              comparison = Number(aValue) - Number(bValue);
              break;
            case "date":
              comparison = new Date(aValue) - new Date(bValue);
              break;
            default:
              comparison = String(aValue).localeCompare(String(bValue));
          }
        }

        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    return processedRows;
  }, [rows, searchQuery, sortConfig, displayColumns, externalSorting, externalSearch]);

  // Sayfalama - skip if hideFooter is true (external pagination)
  const paginatedRows = hideFooter
    ? sortedAndFilteredRows
    : sortedAndFilteredRows.slice(
        currentPage * pageSize,
        (currentPage + 1) * pageSize
      );

  const hasToolbar =
    Boolean(title) ||
    Boolean(actions) ||
    Boolean(viewOptions) ||
    (search !== false &&
      (externalSearch ? Boolean(onSearch) : true));

  // Sayfa değiştiğinde ilk sayfaya dön
  useEffect(() => {
    if (!hideFooter) {
      setCurrentPage(0);
    }
  }, [pageSize, searchQuery, sortConfig, hideFooter]);

  const handleColumnResize = (index, width) => {
    setColumnWidths((prev) => ({
      ...prev,
      [index]: width,
    }));
  };

  return (
    <div className={`table-container ${className}`}>
      {/* Başlık ve Arama */}
      {hasToolbar && (
        <div className="table-header">
          {title && <h2>{title}</h2>}
          {search !== false && (
            <div className="table-actions">
              {externalSearch && onSearch ? (
                <div className="d-flex gap-2" style={{ minWidth: "300px" }}>
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchInput}
                    onChange={(value) => {
                      setSearchInput(value);
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        onSearch(searchInput);
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    appearance="primary"
                    style={{ alignSelf: "flex-end" }}
                    icon={<FiSearch />}
                    onClick={() => {
                      onSearch(searchInput);
                    }}
                  >
                    Ara
                  </Button>
                </div>
              ) : (
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              )}
            </div>
          )}
          {(viewOptions || actions) && (
            <div className="table-toolbar-actions d-flex gap-2 align-items-center">
              {viewOptions}
              {actions}
            </div>
          )}
        </div>
      )}

      {/* Tablo */}
      <div className="table-wrapper">
        <table className="content-table">
          <thead>
            <tr>
              {displayColumns.map((col, index) => (
                <Th
                  key={index}
                  sortable={col.sortable !== false}
                  sortDirection={
                    sortConfig.key === index ? sortConfig.direction : null
                  }
                  onSort={() => handleSort(index)}
                  width={columnWidths[index]}
                  onResize={(width) => handleColumnResize(index, width)}
                >
                  {col.dataType === "checkbox" && showSelectAll ? (
                    <Checkbox
                      checked={selectAll}
                      onChange={({ checked }) => onSelectAll?.(checked)}
                    />
                  ) : (
                    col.name
                  )}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={displayColumns.length} className="loading-cell">
                  Yükleniyor...
                </td>
              </tr>
            ) : paginatedRows.length === 0 ? (
              <tr>
                <td colSpan={displayColumns.length} className="empty-cell">
                  Veri bulunamadı
                </td>
              </tr>
            ) : (
              paginatedRows
            )}
          </tbody>
          {footer ? <tfoot>{footer}</tfoot> : null}
        </table>
      </div>

      {/* Sayfalama */}
      {!hideFooter && (
        <Pagination
          total={sortedAndFilteredRows.length}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </div>
  );
};

export { Table, Tr, Td, Th };
